import {
  TicketCategory,
  SupportTicket,
  SupportTicketListItem,
  CreateTicketResponse,
} from 'Interfaces/SupportTickets/supportTicketInterfaces';
import { getRequest, postRequest, formDataPostRequest } from 'config/Axios/baseConfig';

interface TicketCategoriesResponse {
  categories: TicketCategory[];
}

interface MyTicketsResponse {
  tickets: SupportTicketListItem[];
  count: number;
}

interface TicketDetailResponse {
  ticket: SupportTicket;
}

interface SyncStatusResponse {
  message: string;
  status: string;
  updated: string;
}

// Extended interfaces for comprehensive data
export interface ComprehensiveTicketData {
  categoryId: number;
  title: string;
  description: string;
  pageRoute: string;
  environment: string;
  priority?: string;
  pageUrl?: string;
  screenResolution?: string;
  sessionId?: string;
  userRole?: string;
  errorStack?: string;
  browserInfo?: Record<string, any>;
  additionalContext?: Record<string, any>;
  attachments?: File[];
  screenshot?: Blob;
}

export const supportTicketServices = {
  /**
   * Get all available ticket categories
   */
  getCategories: (): Promise<TicketCategoriesResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<TicketCategoriesResponse>('support-tickets/categories');
        resolve(data);
      } catch (error) {
        console.log('in services > getCategories, Err===', error);
        reject(error);
      }
    });
  },

  /**
   * Create a comprehensive JSM ticket with maximum information capture
   * Uses JSM endpoints for better ticket management
   */
  createJSMTicket: (ticketData: ComprehensiveTicketData): Promise<CreateTicketResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const formData = new FormData();

        // Required fields
        formData.append('category_id', ticketData.categoryId.toString());
        formData.append('title', ticketData.title);
        formData.append('description', ticketData.description);
        formData.append('page_route', ticketData.pageRoute);
        formData.append('environment', ticketData.environment);

        // Optional comprehensive fields
        if (ticketData.priority) {
          formData.append('priority', ticketData.priority);
        }

        if (ticketData.pageUrl) {
          formData.append('page_url', ticketData.pageUrl);
        }

        if (ticketData.screenResolution) {
          formData.append('screen_resolution', ticketData.screenResolution);
        }

        if (ticketData.sessionId) {
          formData.append('session_id', ticketData.sessionId);
        }

        if (ticketData.userRole) {
          formData.append('user_role', ticketData.userRole);
        }

        if (ticketData.errorStack) {
          formData.append('error_stack', ticketData.errorStack);
        }

        // Browser information (as JSON string)
        if (ticketData.browserInfo) {
          formData.append('browser_info', JSON.stringify(ticketData.browserInfo));
        }

        // Additional context (as JSON string)
        if (ticketData.additionalContext) {
          formData.append('additional_context', JSON.stringify(ticketData.additionalContext));
        }

        // Append screenshot if available
        if (ticketData.screenshot) {
          formData.append('attachments', ticketData.screenshot, 'screenshot.jpg');
        }

        // Append other file attachments
        if (ticketData.attachments && ticketData.attachments.length > 0) {
          ticketData.attachments.forEach((file) => {
            formData.append('attachments', file);
          });
        }

        // Use JSM endpoint
        const { data } = await formDataPostRequest<CreateTicketResponse>(
          'support-tickets/jsm/create',
          formData
        );
        resolve(data);
      } catch (error) {
        console.log('in services > createJSMTicket, Err===', error);
        reject(error);
      }
    });
  },

  /**
   * Legacy create ticket method (kept for backward compatibility)
   * @deprecated Use createJSMTicket instead
   */
  createTicket: (
    categoryId: number,
    title: string,
    description: string,
    pageRoute: string,
    environment: string,
    attachments?: File[]
  ): Promise<CreateTicketResponse> => {
    // Forward to JSM ticket creation
    return supportTicketServices.createJSMTicket({
      categoryId,
      title,
      description,
      pageRoute,
      environment,
      attachments,
    });
  },

  /**
   * Get all JSM tickets created by the current user
   */
  getMyTickets: (searchQuery?: string): Promise<MyTicketsResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        // Use JSM endpoint
        const url = searchQuery
          ? `support-tickets/jsm/my-tickets?search=${encodeURIComponent(searchQuery)}`
          : 'support-tickets/jsm/my-tickets';

        const { data } = await getRequest<MyTicketsResponse>(url);
        resolve(data);
      } catch (error) {
        console.log('in services > getMyTickets, Err===', error);
        reject(error);
      }
    });
  },

  /**
   * Get detailed information about a specific JSM ticket
   */
  getTicketDetail: (ticketId: string): Promise<TicketDetailResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        // Use JSM endpoint
        const { data } = await getRequest<TicketDetailResponse>(
          `support-tickets/jsm/ticket/${ticketId}`
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getTicketDetail, Err===', error);
        reject(error);
      }
    });
  },

  /**
   * Sync ticket status from Jira (kept for compatibility, but JSM always returns live data)
   */
  syncTicketStatus: (ticketId: string): Promise<SyncStatusResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        // JSM tickets are always live, but we can still support this endpoint
        const { data } = await postRequest<SyncStatusResponse>(
          `support-tickets/ticket/${ticketId}/sync-status`,
          {}
        );
        resolve(data);
      } catch (error) {
        console.log('in services > syncTicketStatus, Err===', error);
        reject(error);
      }
    });
  },
};
